import SafetyInsights from '../../components/SafetyInsights';
import { useState } from 'react';
import { getDriverSafetyInsuranceReport } from '../../services/safetyService';
import { downloadSafetyInsurancePdf } from '../../utils/safetyInsurancePdf';

const monthValue = (date = new Date()) => date.toISOString().slice(0, 7);

function monthsBefore(endMonth, count) {
  const [year, month] = endMonth.split('-').map(Number);
  return monthValue(new Date(Date.UTC(year, month - count, 1)));
}

const formatScore = (value) => (value == null ? 'Not qualified' : Number(value).toFixed(1));

export default function InsuranceSafetyReport() {
  const endMonth = monthValue();
  const [filters, setFilters] = useState({
    from: monthsBefore(endMonth, 12),
    to: endMonth,
    driverId: '',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const applyDuration = (months) => {
    setFilters((current) => ({ ...current, from: monthsBefore(current.to, months) }));
  };

  const loadReport = async ({ download = false } = {}) => {
    if (!filters.from || !filters.to || filters.from > filters.to) {
      setError('Choose a valid start and end month.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getDriverSafetyInsuranceReport({
        from: filters.from,
        to: filters.to,
        driverId: filters.driverId.trim() || undefined,
      });
      setData(response.data);
      if (download) downloadSafetyInsurancePdf(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to build the insurance safety report.');
    } finally {
      setLoading(false);
    }
  };

  const summary = data?.summary;
  const acknowledgements = summary?.acknowledgements;

  return (
    <>
      <div className="panel" style={{ marginBottom: 18 }}>
        <div className="panel-header">
          <div>
            <h3>Insurance renewal safety summary</h3>
            <p className="panel-subtitle">
              Produce an underwriting-ready PDF with fleet scoring, mileage, event trends,
              expanded safety insights, driver detail, and safety notice acknowledgements.
            </p>
          </div>
          <span className="badge badge-success">PDF report</span>
        </div>
        <div className="panel-body">
          <div className="toolbar" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
            {[3, 6, 12, 24].map((months) => (
              <button key={months} type="button" className="btn btn-ghost" onClick={() => applyDuration(months)}>
                Last {months} months
              </button>
            ))}
          </div>
          <div className="form-grid" style={{ alignItems: 'end' }}>
            <div>
              <label htmlFor="insuranceFrom">Starting month</label>
              <input id="insuranceFrom" type="month" max={endMonth} value={filters.from} onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))} />
            </div>
            <div>
              <label htmlFor="insuranceTo">Through month</label>
              <input id="insuranceTo" type="month" max={endMonth} value={filters.to} onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))} />
            </div>
            <div>
              <label htmlFor="insuranceDriver">Driver ID <span className="hint">(optional)</span></label>
              <input id="insuranceDriver" value={filters.driverId} placeholder="All drivers" onChange={(event) => setFilters((current) => ({ ...current, driverId: event.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-ghost" disabled={loading} onClick={() => loadReport()}>
                {loading ? 'Building...' : 'Preview'}
              </button>
              <button type="button" className="btn btn-primary" disabled={loading} onClick={() => loadReport({ download: true })}>
                {loading ? 'Building PDF...' : 'Download PDF'}
              </button>
            </div>
          </div>
          <p className="hint" style={{ marginTop: 12 }}>
            Choose any inclusive range up to 60 months. The default is the latest 12 calendar months.
          </p>
        </div>
      </div>

      {error ? <div className="feedback error" style={{ marginBottom: 18 }}>{error}</div> : null}

      {summary ? (
        <>
          <section className="ops-briefing-metrics" aria-label="Insurance report preview">
            <div className="ops-briefing-metric ops-briefing-metric--info"><span>Fleet safety score</span><strong>{formatScore(summary.fleetScore)}</strong><em>mileage-weighted qualified score</em></div>
            <div className="ops-briefing-metric ops-briefing-metric--good"><span>Valid mileage</span><strong>{Number(summary.validMiles || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</strong><em>{Math.round(Number(summary.scoredCoverage || 0) * 100)}% scored coverage</em></div>
            <div className="ops-briefing-metric ops-briefing-metric--warning"><span>Road events</span><strong>{Number(summary.totalEvents || 0).toLocaleString()}</strong><em>{Number(summary.eventRate ?? summary.eventRatePer100Miles ?? 0).toFixed(2)} per {Number(summary.eventRateBasisMiles || 100).toLocaleString()} miles</em></div>
            <div className="ops-briefing-metric"><span>Safety participation</span><strong>{acknowledgements?.rate == null ? 'N/A' : `${Math.round(acknowledgements.rate * 100)}%`}</strong><em>{acknowledgements?.acknowledged || 0} of {acknowledgements?.delivered || 0} acknowledged</em></div>
          </section>
          <SafetyInsights insights={summary.safetyInsights} />
          <div className="panel" style={{ marginTop: 18 }}>
            <div className="panel-header">
              <div>
                <h3>PDF contents</h3>
                <p className="panel-subtitle">Prepared for {data.company?.name || 'the fleet'} covering {data.period?.from} through {data.period?.to}.</p>
              </div>
            </div>
            <div className="panel-body">
              <p style={{ marginTop: 0 }}>The download includes company identity and contact details, a fleet performance summary, monthly score and mileage trend, road-event rates, severe events, speeding duration, repeated events, event-free drivers, safety notice acknowledgements, a plain-English explanation of the numbers, and driver-level results.</p>
              <p className="hint" style={{ marginBottom: 0 }}>Provisional months remain visible and are excluded from the fleet score until the configured minimum mileage is reached.</p>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
